import React, {useEffect} from "react";
import {
    IonButton,
    IonContent, IonIcon,
    IonLoading,
    IonPage, useIonModal
} from "@ionic/react";
import {
    arrowDownOutline, arrowDownSharp, arrowUpOutline, arrowUpSharp,
    personOutline, personSharp
} from "ionicons/icons";
import './AcaViewContact.css';
import {ContactTypeBrief} from "./types";
import {useMutation} from "@apollo/client";
import {
    ACA_CREATE_NOTE,
    ACA_GET_CONTACTS,
} from "../../schemas/aca";
import {useHistory} from "react-router";
import {formatDatetime, formatPhone, formatStatus} from "../../utils";
import {AcaHeader} from "./AcaHeader";
import ModalNote from "./modals/ModalNote";
import useAuth from "../../hooks/useAuth";
import PagingButtons from "../../components/PagingButtons";


const AcaViewContacts: React.FC<any> = () => {

    const {claims} = useAuth();
    const history = useHistory();
    const DEFAULT_LOADING_MESSAGE: string = 'Please wait...';
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string>(DEFAULT_LOADING_MESSAGE);


    const [contacts, setContacts] = React.useState<ContactTypeBrief[] | undefined>(undefined);

    const [acaGetContactsMutation] = useMutation(ACA_GET_CONTACTS);
    const [tier1, setTier1] = React.useState<boolean>(false);
    const [tier2, setTier2] = React.useState<boolean>(false);
    const [manager, setManager] = React.useState<boolean>(false);

    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [numPages, setNumPages] = React.useState<number>(1);
    const [orderColumn, setOrderColumn] = React.useState<string>('CREATED_DATETIME');
    const [orderDirection, setOrderDirection] = React.useState<string>('DESC');
    const [totalCount, setTotalCount] = React.useState<number | undefined>(undefined);


    useEffect(() => {
        // check for roles
        if (!claims.rules.includes('AcaView')) {
            history.push('/');
            return;
        }
        if (claims.rules.includes('AcaEditTier2')) {
            setTier2(true);
        }
        if (claims.rules.includes('AcaEditTier1')) {
            setTier1(true);
        }
        if (claims.rules.includes('AcaManager')) {
            setManager(true);
        }
        loadData(1);
    }, []);


    const loadData = async (page: number, column?: string, direction?: string) => {
        setIsLoading(true);
        if (!column) {
            column = orderColumn;
        }
        if (!direction) {
            direction = orderDirection;
        }
        try {
            let contactsResult = await acaGetContactsMutation({variables: {data: {currentPage: page, pageSize: 50, orderColumn: column, orderDirection: direction}}})
            if (!contactsResult.data.acaGetContacts.success) {
                return alert(contactsResult.data.acaGetContacts.error);
            }
            setContacts(contactsResult.data.acaGetContacts.contacts);
            setNumPages(contactsResult.data.acaGetContacts.pagination.numOfPages);
            setTotalCount(contactsResult.data.acaGetContacts.pagination.totalCount);
        } catch (e: any) {
            console.error(e);
            alert(e.message);
        } finally {
            setIsLoading(false);
        }
    }
    const changePage = async (page: number) => {
        setCurrentPage(Number(page));
        await loadData(page);
    }
    const toggleOrdering = async (column: string) => {
        if (column != orderColumn) {
            setOrderColumn(column);
            setOrderDirection('DESC');
            await loadData(1, column, 'DESC');
        } else {
            if (orderDirection === 'DESC') {
                setOrderDirection('ASC');
                await loadData(1, column, 'ASC');
            } else {
                setOrderDirection('DESC');
                await loadData(1, column, 'DESC');
            }
        }
    };


    return (
        <IonPage>

            <AcaHeader pageTitle="ACA Contacts"></AcaHeader>
            <IonLoading isOpen={isLoading} message={loadingMessage}/>
            <IonContent fullscreen className="aca-contacts">
                <div style={{padding: '10px'}}>
                    <p><b>Total Contact Count:</b> {totalCount}</p>
                    {numPages &&
                        <div style={{float:'right', display: 'inline-block'}}><PagingButtons totalPages={numPages} currentPage={currentPage} onPageChange={changePage}></PagingButtons></div>
                    }
                    <table className="aca-healthcare" style={{width: '100%'}}>
                        <thead>
                            <tr>
                                <th>Contact ID</th>
                                <th onClick={() => toggleOrdering('FIRST_NAME')} style={{cursor: 'pointer'}}>First Name {orderColumn === 'FIRST_NAME' && orderDirection === 'DESC' &&
                                        <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                    }
                                    {orderColumn === 'FIRST_NAME' && orderDirection === 'ASC' &&
                                        <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                    }</th>
                                <th onClick={() => toggleOrdering('LAST_NAME')} style={{cursor: 'pointer'}}>Last Name {orderColumn === 'LAST_NAME' && orderDirection === 'DESC' &&
                                        <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                    }
                                    {orderColumn === 'LAST_NAME' && orderDirection === 'ASC' &&
                                        <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                    }</th>
                                <th onClick={() => toggleOrdering('STATUS')} style={{cursor: 'pointer'}}>Status {orderColumn === 'STATUS' && orderDirection === 'DESC' &&
                                        <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                    }
                                    {orderColumn === 'STATUS' && orderDirection === 'ASC' &&
                                        <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                    }</th>
                                <th onClick={() => toggleOrdering('PHONE')} style={{cursor: 'pointer'}}>Phone {orderColumn === 'PHONE' && orderDirection === 'DESC' &&
                                        <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                    }
                                    {orderColumn === 'PHONE' && orderDirection === 'ASC' &&
                                        <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                    }</th>
                                <th onClick={() => toggleOrdering('EMAIL')} style={{cursor: 'pointer'}}>Email {orderColumn === 'EMAIL' && orderDirection === 'DESC' &&
                                        <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                    }
                                    {orderColumn === 'EMAIL' && orderDirection === 'ASC' &&
                                        <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                    }</th>
                                <th>Address</th>
                                <th onClick={() => toggleOrdering('CREATED_DATETIME')} style={{cursor: 'pointer'}}>Created Datetime {orderColumn === 'CREATED_DATETIME' && orderDirection === 'DESC' &&
                                        <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                    }
                                    {orderColumn === 'CREATED_DATETIME' && orderDirection === 'ASC' &&
                                        <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                    }</th>
                                <th onClick={() => toggleOrdering('LAST_UPDATED_DATETIME')} style={{cursor: 'pointer'}}>Last Updated Datetime {orderColumn === 'LAST_UPDATED_DATETIME' && orderDirection === 'DESC' &&
                                        <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                    }
                                    {orderColumn === 'LAST_UPDATED_DATETIME' && orderDirection === 'ASC' &&
                                        <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                    }</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {contacts && contacts.map((contact, index) => {
                                return (
                                    <tr key={'contact_'+index}>
                                        <td style={{textAlign: 'center'}}>{contact.contactId}</td>
                                        <td>{contact.firstName}</td>
                                        <td>{contact.lastName}</td>
                                        <td>{formatStatus(contact.status)}</td>
                                        <td>{formatPhone(contact.phone)}</td>
                                        <td>{contact.email}</td>
                                        <td>{contact.addressLine1}<br />
                                            {contact.addressLine2 && <>{contact.addressLine2}<br /></>}
                                            {contact.addressCity}, {contact.addressState} {contact.addressZip}</td>
                                        <td>{formatDatetime(contact.createdDatetime)}</td>
                                        <td>{formatDatetime(contact.lastUpdatedDatetime)}</td>
                                        <td><IonButton routerLink={'/aca/view-contact/' + contact.contactId} routerDirection="none" fill="clear" size="small" className="xs-button"><IonIcon md={personOutline} ios={personSharp}></IonIcon></IonButton></td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                    {numPages &&
                        <div style={{float:'right', display: 'inline-block'}}><PagingButtons totalPages={numPages} currentPage={currentPage} onPageChange={changePage}></PagingButtons></div>
                    }
                    <div style={{height: '60px'}}></div>
                </div>
            </IonContent>
        </IonPage>
    )
}

export default AcaViewContacts;